<template>
  <AisCustomHits>
    <div
      v-if="shouldShowSneakPeek && firstSneakPeekBrandHit"
      class="sneak-peek-container"
    >
      <SneakPeekContainer
        :brand-id="firstSneakPeekBrandHit.id.toString()"
        :is-new-page="isNewCategory"
      />
    </div>
    <CatalogGrid
      :key="redraw"
      :has-error="status === 'error'"
      :is-searching="isSearching"
      :tiles="catalogItems"
      :tiles-per-row="tilesPerRow"
      :config="gridConfig"
      :filters-shown="filtersShown"
    >
      <template #error>
        <AkParagraph
          class="ds-mb-7"
          data-testid="noResultMessage"
        >
          {{ $t('@@core_discovery.search.no_results.msg.error') }}
        </AkParagraph>
      </template>
      <template #skeleton>
        <SkeletonCard :tile-type="config.template" />
      </template>
      <template
        v-for="slot in relatedCollections.slots"
        :key="`${slot.name}-${slot.insertionIndex}-banner`"
        #[slot.name]
      >
        <component
          :is="slot.content"
          v-if="isVNode(slot.content)"
          :filters-shown="filtersShown"
          @collection-clicked="handleRelatedCollectionClicked"
        />
        <CollectionBanner
          v-else
          :section="slot.content"
          data-testid="collection-banner"
          :filters-shown="filtersShown"
          @collection-clicked="handleRelatedCollectionClicked"
        />
      </template>
      <template
        v-for="slot in sneakPeekSlots"
        :key="`${slot.name}-sneak-peek`"
        #[slot.name]
      >
        <div class="sneak-peek-container">
          <SneakPeekContainer
            :brand-id="slot.brandId"
            :is-new-page="isNewCategory"
          />
        </div>
      </template>
      <template #default="{ tile, index }">
        <CatalogTile
          :id="tile.id"
          :key="tile.id"
          class="ds-h-full"
          :config="config"
          :item="tile"
          :page="page"
          :index="index"
          data-testid="product"
        >
          <template #product="productProps">
            <slot
              name="product"
              v-bind="productProps"
            />
          </template>
        </CatalogTile>
      </template>
      <template #noResults>
        <slot name="noResults" />
      </template>
    </CatalogGrid>
  </AisCustomHits>
</template>

<script lang="ts">
import { watch, watchEffect, defineComponent, ref, computed, provide, PropType, nextTick, isVNode } from 'vue';
import CatalogTile from '../components/tile/catalog-tile.vue';
import AisCustomHits from '../components/custom-ais/custom-hits.vue';
import { Config, CatalogGridInsertion } from '@bc/discovery/domain/catalog';
import RootStoreHelpers from '@monolith/legacy/store/helpers';
import { useAisState } from '../composables/use-ais-state';
import useGlobals from '@core/composables/use-globals';
import { ProductHit } from '@monolith/legacy/types/product';
import { type BrandHit } from '@bc/discovery/domain/catalog';
import { CollectionBanner, CollectionTypes } from '@bc/discovery/ui/collection';
import { SkeletonCard } from '@bc/discovery/ui/cards/skeleton-card';
import { CatalogGrid, CatalogUiTypes } from '@bc/discovery/ui/catalog';
import { ProductAdapter } from '@bc/discovery/domain/product';
import { DiscoveryTrackingEvents } from '@bc/discovery/domain/tracking';
import { useCatalogTilesPerRow } from '../composables/use-catalog-tiles-per-row';
import useCatalogTrackingData from '../composables/use-catalog-tracking-data';
import { mapRefinements } from '../services/tracking';
import { useEventBus } from '@bc/shared';
import type { ESProductHit, ESBrandHit, ESSponsoredProductHit } from '@bc/discovery/domain/catalog';
import { SpecialTileGlobal } from '@client/monolith/legacy/modules/promote-engage/promotion-tile/types';
import { useRoute } from 'vue-router';
import SneakPeekContainer from '@client/modules/discovery/feature/brands-sneak-peek/components/sneak-peek-container.vue';
import { isEnabled } from '@monolith/legacy/services/features';
import { useFundedDiscounts, FUNDED_DISCOUNTS_KEY } from '@bc/loyalty';

export default defineComponent({
  name: 'CatalogHits',
  components: { CollectionBanner, SkeletonCard, AisCustomHits, CatalogGrid, CatalogTile, SneakPeekContainer },
  props: {
    config: {
      type: Object as PropType<Config>,
      required: true,
    },
    relatedCollectionInsertions: {
      type: Object as PropType<CatalogGridInsertion>,
      default: undefined,
    },
    filtersShown: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { eventBus } = useEventBus();
    const route = useRoute();
    const isNewCategory = computed(() => route.path.endsWith('/new'));
    const { user } = RootStoreHelpers.useGetters(['user']);

    const {
      aisState: { status, hits, pagination, refinements },
    } = useAisState();
    const items = computed(() => transformItems(hits.value));
    const page = computed(() => pagination.value.page);
    const catalogTrackingData = useCatalogTrackingData();
    const trackingValueProposition = computed(() => ({
      value_proposition: catalogTrackingData.valueProposition.value,
      value_proposition_id: catalogTrackingData.valuePropositionId.value,
    }));
    const trackingFilters = computed(() => mapRefinements(catalogTrackingData.config.value, refinements.value));

    const { lang } = useGlobals();
    const { endProgressBar, storeProductsFromAlgolia } = RootStoreHelpers.useActions([
      'endProgressBar',
      'storeProductsFromAlgolia',
    ]);
    const tilesPerRow = useCatalogTilesPerRow();

    const isProductHit = (item: ProductHit | BrandHit): item is ProductHit => 'brand' in item;

    // Provide funded discounts to all child CardProduct instances (one API call per page instead of N)
    const { ensureLoaded: ensureFundedDiscountsLoaded, getOffer, hasOffer } = useFundedDiscounts();
    provide(FUNDED_DISCOUNTS_KEY, { getOffer, hasOffer });

    watch(hits, (newHits) => {
      if (!newHits?.length) {
        return;
      }
      const brandUuids = [...new Set(
        newHits
          .filter(isProductHit)
          .map((item) => item.brand?.uuid)
          .filter(Boolean)
      )] as string[];
      if (brandUuids.length > 0) {
        ensureFundedDiscountsLoaded(brandUuids).catch(() => {});
      }
    }, { immediate: true });

    const transformItems = (items: Array<ProductHit | BrandHit>) => {
      let transformedItems = [];
      if (items?.length) {
        transformedItems = items.map((item) => ProductAdapter.hitAdapter(lang, item));
        if (items?.every(isProductHit)) {
          storeProductsFromAlgolia({ products: transformedItems });
        }
      }
      endProgressBar();
      return transformedItems;
    };

    const isSearching = computed(() => status.value === 'loading' || status.value === 'stalled' || isWaitingForSlots.value);

    const relatedCollections = ref({
      slots: [],
      config: {},
    });
    const gridConfig = ref<CatalogUiTypes.GridConfig>(undefined);

    const isBrandHit = (hit: ESProductHit | ESBrandHit | ESSponsoredProductHit | SpecialTileGlobal): hit is ESBrandHit => {
      return 'brand_tags' in hit;
    };

    const sneakPeekBrandHits = computed(() => {
      return items.value.filter(isBrandHit).filter((hit) => hit.metadata.sneak_peek === true);
    });

    // Filter sneak peek brands that have products available
    const availableSneakPeekBrandHits = computed(() => {
      return sneakPeekBrandHits.value.filter((hit) => {
        // Check if brand has featured_products with at least one product
        return hit.featured_products && Object.keys(hit.featured_products).length > 0;
      });
    });

    const shouldShowSneakPeek = computed<boolean>(() => {
      return Boolean(isNewCategory.value && availableSneakPeekBrandHits.value.length > 0 && isEnabled('RET-3764') && user.value);
    });

    const catalogItems = computed(() => {
      if (!isNewCategory.value || sneakPeekBrandHits.value.length === 0) {
        return items.value;
      }

      // Get IDs of sneak peek brand hits to exclude
      const sneakPeekBrandIds = sneakPeekBrandHits.value.map((hit) => hit.id);

      // Filter out the sneak peek brand hits from regular items
      const filteredItems = items.value.filter((item) => {
        // If it's a brand hit, check if it's in the sneak peek list
        if (isBrandHit(item) && shouldShowSneakPeek.value) {
          const shouldExclude = sneakPeekBrandIds.includes(item.id);
          return !shouldExclude;
        }
        return true;
      });

      return filteredItems;
    });

    // First sneak peek (shown at the top) - with fallback for brands with no products
    const firstSneakPeekBrandHit = computed(() => {
      return availableSneakPeekBrandHits.value[0];
    });

    // Calculate sneak peek insertion positions and distribute remaining brand hits
    const sneakPeekInsertions = computed(() => {
      if (!shouldShowSneakPeek.value || availableSneakPeekBrandHits.value.length <= 1) {
        return { slots: [], config: {} };
      }

      const SNEAK_PEEK_INTERVAL = 4; // Every 4 rows
      const totalItems = catalogItems.value.length;
      const itemsPerRow = tilesPerRow.value;

      // Skip the first available brand hit since it's shown at the top
      const remainingAvailableBrandHits = availableSneakPeekBrandHits.value.slice(1);

      // Calculate total rows based on current grid
      const totalRows = Math.ceil(totalItems / itemsPerRow);

      // Calculate how many sneak peek insertion points we can have (every 4 rows)
      const maxInsertionPoints = Math.floor(totalRows / SNEAK_PEEK_INTERVAL);
      const slotsToCreate = Math.min(maxInsertionPoints, remainingAvailableBrandHits.length);

      const slots = [];
      const config = {};

      for (let i = 0; i < slotsToCreate; i++) {
        const brandHit = remainingAvailableBrandHits[i];
        const slotName = `sneak-peek-insertion-${i}`;
        // Insert every 4 rows: row 5, 9, 13, etc.
        const rowPosition = (i + 1) * SNEAK_PEEK_INTERVAL + 1;

        slots.push({
          name: slotName,
          brandId: brandHit.id.toString(),
        });

        config[slotName] = {
          row: rowPosition,
        };
      }

      return { slots, config };
    });

    const sneakPeekSlots = computed(() => sneakPeekInsertions.value.slots);

    watchEffect(() => {
      const value = {
        slots: [],
        config: {},
      };

      // Add related collection insertions
      if (props.relatedCollectionInsertions?.status === 'idle' && hits.value) {
        value.slots = props.relatedCollectionInsertions.slots;
        value.config = props.relatedCollectionInsertions.slots.reduce((acc, slot) => {
          const insertionIndex =
            'function' === typeof slot.insertionRowIndex
              ? slot.insertionRowIndex(hits.value.length, tilesPerRow.value)
              : slot.insertionRowIndex;
          acc[slot.name] = {
            row: insertionIndex + 1,
          };
          return acc;
        }, {});
      }

      // Merge sneak peek insertions
      if (shouldShowSneakPeek.value) {
        value.config = {
          ...value.config,
          ...sneakPeekInsertions.value.config,
        };
      }

      nextTick(() => {
        relatedCollections.value = value;
        gridConfig.value = value.config;
      });
    });

    const handleRelatedCollectionClicked = (collection: CollectionTypes.CollectionCard) => {
      eventBus.emit(DiscoveryTrackingEvents.RELATED_COLLECTION_CLICKED, {
        collectionId: collection.uuid,
        itemType: collection.itemType,
      });
    };

    const isWaitingForSlots = computed(() => props.relatedCollectionInsertions?.status === 'loading');

    const redraw = ref(0);
    watch(
      status,
      () => {
        if (status.value === 'idle') {
          nextTick(() => {
            redraw.value = redraw.value + 1;
          });
        }
      },
      { immediate: true }
    );

    return {
      isSearching,
      redraw,
      status,
      items,
      page,
      tilesPerRow,
      gridConfig,
      handleRelatedCollectionClicked,
      isWaitingForSlots,
      relatedCollections,
      trackingFilters,
      trackingValueProposition,
      transformItems,
      ...catalogTrackingData,
      isVNode,
      isNewCategory,
      sneakPeekBrandHits,
      availableSneakPeekBrandHits,
      firstSneakPeekBrandHit,
      catalogItems,
      shouldShowSneakPeek,
      sneakPeekSlots,
    };
  },
});
</script>

<style lang="scss" scoped>
.sneak-peek-container {
  @apply ds-mb-[40px];
}
</style>
